import {
  SummaryFigures, Project, AllocationRow, Trend, TrendPoint, NonBillableClassification,
} from '../../../types';
import { getWeekNumber, weekToDateRange } from '../../../lib/utils';
import {
  RangeState, RangeMode, PeriodKind, PeriodSel, PeriodOption, WeekOption, ResolvedRange,
  SummaryCardDef, EmployeeOption, AllocationChartData, AllocationDatum, AllocationSeries, PieSlice, TrendMetric, TrendMetricDef, TrendDatum, FIRST_YEAR,
} from './interface';

// ── Date range ───────────────────────────────────────────────────────────────

export const DEFAULT_RANGE: RangeState = { kind: 'preset', mode: 'mtd' };

export const RANGE_MODES: { value: RangeMode; label: string }[] = [
  { value: 'ytd', label: 'Year to Date' },
  { value: 'qtd', label: 'Quarter to Date' },
  { value: 'mtd', label: 'Month to Date' },
  { value: 'range', label: 'From-To Range' },
  { value: 'custom', label: 'Custom' },
];

type CustomRange = Extract<RangeState, { kind: 'custom' }>;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const PERIOD_COUNTS: Record<PeriodKind, number> = { half: 2, quarter: 4, month: 12 };
const MONTHS_PER: Record<PeriodKind, number> = { half: 6, quarter: 3, month: 1 };
const DAY_MS = 86_400_000;

const utcDay = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d));
const fmt = (d: Date) => d.toISOString().slice(0, 10);
const parseDay = (s: string) => new Date(`${s}T00:00:00.000Z`);
const short = (iso: string) =>
  parseDay(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** Years offered: FIRST_YEAR through the current one, newest first. Never a future year. */
export function availableYears(now: Date = new Date()): number[] {
  const years: number[] = [];
  for (let y = now.getUTCFullYear(); y >= FIRST_YEAR; y--) years.push(y);
  return years.length > 0 ? years : [FIRST_YEAR];
}

/** The whole period, before it is cut off at today. */
function periodBounds(year: number, p: PeriodSel): ResolvedRange {
  const start = p.index * MONTHS_PER[p.kind];
  return { from: fmt(utcDay(year, start, 1)), to: fmt(utcDay(year, start + MONTHS_PER[p.kind], 0)) };
}

export const periodId = (p: PeriodSel) => `${p.kind}:${p.index}`;

export function parsePeriodId(id: string): PeriodSel | undefined {
  const [kind, raw] = id.split(':');
  const index = Number(raw);
  if (kind !== 'half' && kind !== 'quarter' && kind !== 'month') return undefined;
  return Number.isInteger(index) && index >= 0 && index < PERIOD_COUNTS[kind] ? { kind, index } : undefined;
}

/** Halves, quarters and months of a year. Ones that start after today are disabled. */
export function periodOptions(year: number, now: Date = new Date()): PeriodOption[] {
  const today = fmt(now);
  const make = (kind: PeriodKind, index: number, label: string): PeriodOption => {
    const sel = { kind, index };
    const { from, to } = periodBounds(year, sel);
    return { id: periodId(sel), label, from, to, disabled: from > today };
  };
  return [
    ...[0, 1].map((i) => make('half', i, `Half ${i + 1}`)),
    ...[0, 1, 2, 3].map((i) => make('quarter', i, `Quarter ${i + 1}`)),
    ...MONTH_NAMES.map((name, i) => make('month', i, name)),
  ];
}

/** Weeks of the year that overlap the period (the whole year when there is none), clipped to it. */
export function weekOptions(year: number, period: PeriodSel | undefined, now: Date = new Date()): WeekOption[] {
  const today = fmt(now);
  const bounds = period ? periodBounds(year, period) : { from: fmt(utcDay(year, 0, 1)), to: fmt(utcDay(year, 11, 31)) };
  const lastWeek = getWeekNumber(`${year}-12-31`);
  const options: WeekOption[] = [];
  for (let week = 1; week <= lastWeek; week++) {
    const w = weekToDateRange(year, week);
    if (w.to < bounds.from || w.from > bounds.to) continue;
    const from = w.from > bounds.from ? w.from : bounds.from;
    const to = w.to < bounds.to ? w.to : bounds.to;
    options.push({ week, label: `Week ${week} · ${short(from)} – ${short(to)}`, from, to, disabled: from > today });
  }
  return options;
}

/**
 * Makes a custom selection consistent: the year must be offered, the period
 * must have started, and the week must belong to the period. Anything that is
 * not is dropped, so a level never outlives a change to the one above it.
 */
export function normalizeCustom(state: CustomRange, now: Date = new Date()): CustomRange {
  const years = availableYears(now);
  const year = years.includes(state.year) ? state.year : years[0];
  const periodOk = state.period
    ? periodOptions(year, now).some((o) => o.id === periodId(state.period!) && !o.disabled)
    : false;
  const period = periodOk ? state.period : undefined;
  const weekOk = state.week !== undefined
    && weekOptions(year, period, now).some((o) => o.week === state.week && !o.disabled);
  const week = weekOk ? state.week : undefined;
  return { kind: 'custom', year, ...(period ? { period } : {}), ...(week !== undefined ? { week } : {}) };
}

/**
 * The dates a range covers. It never goes past today: available hours count
 * every working day in the range, so a future end date would understate
 * utilization.
 */
export function resolveRange(state: RangeState, now: Date = new Date()): ResolvedRange {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const today = fmt(now);
  let from: string;
  let to: string;

  if (state.kind === 'preset') {
    const startMonth = state.mode === 'ytd' ? 0 : state.mode === 'qtd' ? Math.floor(m / 3) * 3 : m;
    from = fmt(utcDay(y, startMonth, 1));
    to = today;
  } else if (state.kind === 'range') {
    from = state.from;
    to = state.to;
  } else {
    const bounds = state.period
      ? periodBounds(state.year, state.period)
      : { from: fmt(utcDay(state.year, 0, 1)), to: fmt(utcDay(state.year, 11, 31)) };
    from = bounds.from;
    to = bounds.to;
    if (state.week !== undefined) {
      const w = weekToDateRange(state.year, state.week);
      if (w.from > from) from = w.from;
      if (w.to < to) to = w.to;
    }
  }

  if (to > today) to = today;
  if (from > to) from = to;
  return { from, to };
}

/** A YYYY-MM-DD that is a real calendar day: 2026-02-30 has the right shape but is not one. */
export function isRealDay(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const time = parseDay(value).getTime();
  return !Number.isNaN(time) && fmt(new Date(time)) === value;
}

/** Why a From / To pair cannot be used, in words for the user, or null when it can. */
export function rangeIssue(from: string, to: string, now: Date = new Date()): string | null {
  if (!isRealDay(from) || !isRealDay(to)) return 'Pick both dates.';
  const today = fmt(now);
  if (from > today || to > today) return "Dates can't be after today.";
  if (from > to) return 'From is after To.';
  return null;
}

/** "Oct 1 – Oct 29, 2026", or "Oct 29, 2026" for a single day. */
export function formatRangeLabel(from: string, to: string): string {
  const f = parseDay(from);
  const t = parseDay(to);
  const part = (d: Date, withYear: boolean) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
  if (from === to) return part(t, true);
  const sameYear = f.getUTCFullYear() === t.getUTCFullYear();
  return `${part(f, !sameYear)} – ${part(t, true)}`;
}

/** Sunday–Thursday days in the range, inclusive. The same rule the backend uses for capacity. */
export function countWorkingDays(from: string, to: string): number {
  let count = 0;
  for (let d = parseDay(from); d <= parseDay(to); d = new Date(d.getTime() + DAY_MS)) {
    if (d.getUTCDay() <= 4) count++;
  }
  return count;
}

// ── Range in the URL ─────────────────────────────────────────────────────────
// `?range=mtd`, `?range=custom&year=2026&period=quarter:1&week=14`, or `?range=range&from=2026-03-12&to=2026-04-09`.

export function serializeRange(state: RangeState): Record<string, string> {
  if (state.kind === 'preset') return { range: state.mode };
  if (state.kind === 'range') return { range: 'range', from: state.from, to: state.to };
  return {
    range: 'custom',
    year: String(state.year),
    ...(state.period ? { period: periodId(state.period) } : {}),
    ...(state.week !== undefined ? { week: String(state.week) } : {}),
  };
}

/** Reads a range back from the URL. Anything unreadable, inconsistent or in the future falls back to the default. */
export function parseRange(params: URLSearchParams, now: Date = new Date()): RangeState {
  const raw = params.get('range');
  if (raw === 'ytd' || raw === 'qtd' || raw === 'mtd') return { kind: 'preset', mode: raw };
  if (raw === 'range') {
    const from = params.get('from') ?? '';
    const to = params.get('to') ?? '';
    return rangeIssue(from, to, now) === null ? { kind: 'range', from, to } : DEFAULT_RANGE;
  }
  if (raw !== 'custom') return DEFAULT_RANGE;

  const year = Number(params.get('year'));
  const periodRaw = params.get('period');
  const weekRaw = params.get('week');
  const period = periodRaw ? parsePeriodId(periodRaw) : undefined;
  const week = weekRaw !== null ? Number(weekRaw) : undefined;
  if (!Number.isInteger(year) || (periodRaw && !period) || (week !== undefined && !Number.isInteger(week))) {
    return DEFAULT_RANGE;
  }

  const candidate: CustomRange = {
    kind: 'custom', year, ...(period ? { period } : {}), ...(week !== undefined ? { week } : {}),
  };
  const clean = normalizeCustom(candidate, now);
  return JSON.stringify(clean) === JSON.stringify(candidate) ? candidate : DEFAULT_RANGE;
}

/** A comma-separated id list from the URL, or undefined when absent. */
export function parseIdList(value: string | null): string[] | undefined {
  const ids = (value ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  return ids.length > 0 ? ids : undefined;
}

// ── KPI summary cards ────────────────────────────────────────────────────────

export const NO_DATA = 'No Data Available';

const hours = (n: number) => `${n}h`;
// Null means no capacity in the range, so a percent would be meaningless.
const percent = (n: number | null) => (n === null ? NO_DATA : `${n}%`);

/** The five KPI cards, in display order, with the formula each one shows in its tooltip. */
export function summaryCards(s: SummaryFigures): SummaryCardDef[] {
  return [
    { key: 'loggedHours', title: 'Logged Hours', value: hours(s.loggedHours),
      formula: 'All logged hours, except Edit Requested and Rejected entries.' },
    { key: 'billableHours', title: 'Billable Hours', value: hours(s.billableHours),
      formula: 'Hours logged on billable projects.' },
    { key: 'nonBillableHours', title: 'Non-billable Hours', value: hours(s.nonBillableHours),
      formula: 'Hours on non-billable projects: POC + Internal + Other.' },
    { key: 'billableUtilization', title: 'Billable Utilization', value: percent(s.billableUtilization),
      formula: 'Billable hours ÷ available hours × 100.' },
    { key: 'effectiveUtilization', title: 'Effective Utilization', value: percent(s.effectiveUtilization),
      formula: '(Billable hours + POC hours) ÷ available hours × 100.' },
  ];
}

// ── Employees and projects ───────────────────────────────────────────────────
// Employees come first: everyone assigned to any project can be picked, and a
// project is only counted while at least one selected employee is assigned to it.

/** People assigned to any project, once each, in name order. */
export function allEmployees(projects: Project[]): EmployeeOption[] {
  const byId = new Map<string, EmployeeOption>();
  for (const p of projects) {
    for (const { user } of p.userProjects ?? []) byId.set(user.id, { id: user.id, name: user.name });
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** Projects with at least one of the selected employees assigned. */
export function enabledProjectIds(projects: Project[], selectedEmployeeIds: string[]): Set<string> {
  const selected = new Set(selectedEmployeeIds);
  const enabled = new Set<string>();
  for (const p of projects) {
    if ((p.userProjects ?? []).some(({ user }) => selected.has(user.id))) enabled.add(p.id);
  }
  return enabled;
}

// ── Charts ───────────────────────────────────────────────────────────────────

/** Categorical colors come from eight fixed slots, in order. A ninth is never invented: it folds into Other. */
export const CHART_SLOTS = 8;

const OTHER_COLOR = 'var(--viz-other)';
const slotColor = (i: number) => `var(--viz-${i + 1})`;

export const OTHER_PROJECTS_KEY = 'other';

/**
 * The bars of the allocation chart: one per employee, each split by project as a
 * percent of that person's capacity. The eight biggest projects (by hours across
 * everyone shown) get their own color; the rest fold into one gray "Other
 * projects" segment. Colors are dealt out in name order, so a project keeps its
 * color while the same set stays on screen.
 */
export function allocationChartData(rows: AllocationRow[], max: number = CHART_SLOTS): AllocationChartData {
  const totals = new Map<string, { name: string; hours: number }>();
  for (const row of rows) {
    for (const p of row.projects) {
      const t = totals.get(p.projectId) ?? { name: p.projectName, hours: 0 };
      t.hours += p.hours;
      totals.set(p.projectId, t);
    }
  }

  const ranked = Array.from(totals, ([id, t]) => ({ id, ...t }))
    .sort((a, b) => b.hours - a.hours || a.name.localeCompare(b.name));
  const shown = ranked.slice(0, max).sort((a, b) => a.name.localeCompare(b.name));
  const keyOf = new Map(shown.map((p) => [p.id, `p:${p.id}`]));

  const series: AllocationSeries[] = shown.map((p, i) => ({ key: `p:${p.id}`, name: p.name, color: slotColor(i) }));
  const folded = ranked.length > shown.length;
  if (folded) series.push({ key: OTHER_PROJECTS_KEY, name: 'Other projects', color: OTHER_COLOR });

  const data = rows.map((row): AllocationDatum => {
    const datum: AllocationDatum = { name: row.name, total: null, availableHours: row.availableHours, hours: {} };
    let total: number | null = null;
    for (const p of row.projects) {
      const key = keyOf.get(p.projectId) ?? OTHER_PROJECTS_KEY;
      datum[key] = ((datum[key] as number | undefined) ?? 0) + (p.percent ?? 0);
      datum.hours[key] = (datum.hours[key] ?? 0) + p.hours;
      total = (total ?? 0) + (p.percent ?? 0);
    }
    for (const key of Object.keys(datum.hours)) {
      datum[key] = Math.round((datum[key] as number) * 100) / 100;
      datum.hours[key] = Math.round(datum.hours[key] * 100) / 100;
    }
    datum.total = total === null ? (row.availableHours > 0 ? 0 : null) : Math.round(total * 100) / 100;
    return datum;
  });

  return { series, data };
}

/** The last series with a value in this bar, the one whose end gets the rounded corner. */
export function lastSeriesKey(datum: AllocationDatum, series: AllocationSeries[]): string | null {
  for (let i = series.length - 1; i >= 0; i--) {
    const value = datum[series[i].key];
    if (typeof value === 'number' && value > 0) return series[i].key;
  }
  return null;
}


// ── Trends ───────────────────────────────────────────────────────────────────

export const TREND_METRICS: Record<TrendMetric, TrendMetricDef> = {
  billable: {
    title: 'Billable Utilization over time',
    subtitle: 'Billable hours over available hours, per period',
    key: 'billableUtilization',
    color: 'var(--viz-1)',
    hoursLabel: 'billable',
  },
  effective: {
    title: 'Effective Utilization over time',
    subtitle: 'Billable plus POC hours over available hours, per period',
    key: 'effectiveUtilization',
    color: 'var(--viz-2)',
    hoursLabel: 'billable + POC',
  },
};

const monthShort = (iso: string, withYear: boolean) =>
  parseDay(iso).toLocaleDateString('en-US', {
    month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
  });

/** The axis label of one bucket: the day, the day the week starts, or the month. */
export function trendAxisLabel(point: TrendPoint, granularity: Trend['granularity'], spansYears: boolean): string {
  if (granularity === 'month') return monthShort(point.from, spansYears);
  return short(point.from);
}

/** The points as the chart draws them, for one measure. */
export function trendData(trend: Trend, metric: TrendMetric): TrendDatum[] {
  const key = TREND_METRICS[metric].key;
  const spansYears = trend.points.length > 0
    && trend.points[0].from.slice(0, 4) !== trend.points[trend.points.length - 1].to.slice(0, 4);
  return trend.points.map((p) => ({
    label: trendAxisLabel(p, trend.granularity, spansYears),
    from: p.from,
    to: p.to,
    workingDays: p.workingDays,
    utilization: p[key],
    hours: metric === 'billable' ? p.billableHours : Math.round((p.billableHours + p.pocHours) * 100) / 100,
    availableHours: p.availableHours,
  }));
}

/** The top of the shared y axis: the highest utilization of either measure, rounded up to 10, never under 10. */
export function trendYMax(trend: Trend | undefined): number {
  const values = (trend?.points ?? []).flatMap((p) => [p.billableUtilization, p.effectiveUtilization])
    .filter((v): v is number => v !== null);
  return Math.max(10, Math.ceil(Math.max(0, ...values) / 10) * 10);
}

/** True when no point has a utilization to plot, so the chart would be empty. */
export function trendIsEmpty(trend: Trend | undefined, metric: TrendMetric): boolean {
  if (!trend) return false;
  const key = TREND_METRICS[metric].key;
  return trend.points.every((p) => p[key] === null);
}

// ── Pie badges ───────────────────────────────────────────────────────────────

/** A slice smaller than this is too thin to hold a badge. The legend still lists it. */
export const PIE_BADGE_MIN_PERCENT = 8;

/** Share of the radius, from the centre, where a slice's badge sits. */
const PIE_BADGE_RADIUS = 0.62;

/**
 * Where the percent badge goes inside a slice: on its middle line, a bit past
 * halfway out. `midAngle` is in degrees, as Recharts gives it. Null for a slice
 * too small to hold one.
 */
export function sliceBadge(
  percent: number,
  cx: number,
  cy: number,
  outerRadius: number,
  midAngle: number,
): { x: number; y: number; text: string } | null {
  if (percent < PIE_BADGE_MIN_PERCENT) return null;
  const radian = (-midAngle * Math.PI) / 180;
  const r = outerRadius * PIE_BADGE_RADIUS;
  return { x: cx + r * Math.cos(radian), y: cy + r * Math.sin(radian), text: `${percent}%` };
}

// ── Non-billable breakdown ───────────────────────────────────────────────────

/** A project label keeps its color on every chart. */
export const CLASSIFICATION_META: Record<NonBillableClassification, { label: string; color: string }> = {
  POC: { label: 'POC', color: 'var(--viz-1)' },
  INTERNAL: { label: 'Internal', color: 'var(--viz-2)' },
  OTHER: { label: 'Other', color: 'var(--viz-3)' },
};

/**
 * The pie of non-billable hours: POC, Internal and Other, each as a share of
 * the non-billable total. A label with no hours has no slice.
 */
export function nonBillableSplit(figures: SummaryFigures): PieSlice[] {
  const total = figures.nonBillableHours;
  if (total <= 0) return [];
  const hoursOf: Record<NonBillableClassification, number> = {
    POC: figures.pocHours,
    INTERNAL: figures.internalHours,
    OTHER: figures.otherHours,
  };
  return (['POC', 'INTERNAL', 'OTHER'] as const)
    .filter((c) => hoursOf[c] > 0)
    .map((c) => ({
      key: c,
      name: CLASSIFICATION_META[c].label,
      hours: hoursOf[c],
      percent: Math.round((hoursOf[c] / total) * 10000) / 100,
      color: CLASSIFICATION_META[c].color,
    }));
}
